import { type LDFlagSet } from 'launchdarkly-js-client-sdk';
import { useMemo } from 'react';
import { matchPath, useLocation } from 'react-router';

import ChevronRightIcon from '@/assets/svg/bx-chevron-right.svg?react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/DropdownMenu';
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem } from '@/components/Sidebar';
import { useAllFeatureFlags } from '@/hooks/useFeatureFlag';
import useInstanceNavigate from '@/hooks/useInstanceNavigate';
import { cn } from '@/lib/utils';
import { useMe } from '@/providers/User';

import { getMenuItems } from './constants';
import { type MenuItemType } from './types';

const isSubmenuItem = (
  item: MenuItemType,
): item is MenuItemType & { type: 'submenu'; children: MenuItemType[] } => {
  return item.type === 'submenu' && Array.isArray(item.children);
};

const isRouteActive = (pathname: string, key: string) => {
  const keyPathname = key.split('?')[0];
  return matchPath({ path: keyPathname, end: false }, pathname) != null;
};

const getSelectedKeys = (pathname: string, menuItems: MenuItemType[]) => {
  const keys: string[] = [];

  menuItems.forEach((item) => {
    if (isSubmenuItem(item)) {
      item.children.forEach((child) => {
        if (isRouteActive(pathname, child.key)) {
          keys.push(child.key);
        }
      });
      return;
    }

    if (isRouteActive(pathname, item.key)) {
      keys.push(item.key);
    }
  });

  return keys;
};

const getSelectedSubmenuKeys = (pathname: string, menuItems: MenuItemType[]) => {
  return menuItems
    .filter(
      (item) =>
        isSubmenuItem(item) && item.children.some((child) => isRouteActive(pathname, child.key)),
    )
    .map((item) => item.key);
};

/**
 * strip out feature-flagged menu items or menu items which cannot be accessed by non-privileged
 * users
 */
const filterMenuItems = (
  menuItems: MenuItemType[],
  flags: LDFlagSet,
  isPaidUser: boolean,
  isAdminUser: boolean,
) => {
  return menuItems.filter((item) => {
    if (!isSubmenuItem(item)) {
      return (
        item.hidden?.(flags) !== true &&
        (!item.isPaid || (isPaidUser && item.isPaid)) &&
        (!item.isAdmin || (isAdminUser && item.isAdmin))
      );
    }

    return item;
  });
};

const wrapMenuItems = (
  menuItems: MenuItemType[],
  flags: LDFlagSet,
  isPaidUser: boolean,
  isAdminUser: boolean,
) => {
  const stripMenuMeta = (item: MenuItemType) => {
    const nextItem = { ...item };
    delete nextItem.hidden;
    delete nextItem.isPaid;
    delete nextItem.isAdmin;
    return nextItem;
  };

  return filterMenuItems(menuItems, flags, isPaidUser, isAdminUser).map((item) => {
    if (!isSubmenuItem(item)) {
      return stripMenuMeta(item);
    }

    const nextChildren = filterMenuItems(item.children, flags, isPaidUser, isAdminUser).map(
      stripMenuMeta,
    );
    const nextItem = stripMenuMeta(item);

    return {
      ...nextItem,
      type: 'submenu' as const,
      children: nextChildren,
    };
  });
};

type Props = {
  className?: string;
  onMenuSelect?: (key: string) => void;
};

const AppMenu = ({ className, onMenuSelect }: Props) => {
  const user = useMe();
  const navigate = useInstanceNavigate();
  const { pathname } = useLocation();
  const flags = useAllFeatureFlags();

  const isAdminUser = !!user?.isAdmin;
  const isPaidUser = !!user?.isPaid;

  const menuItems = useMemo(
    () => wrapMenuItems(getMenuItems(), flags, isPaidUser, isAdminUser),
    [flags, isPaidUser, isAdminUser],
  );

  const selectedKeys = useMemo(() => getSelectedKeys(pathname, menuItems), [pathname, menuItems]);

  const selectedSubmenuKeys = useMemo(
    () => getSelectedSubmenuKeys(pathname, menuItems),
    [pathname, menuItems],
  );

  const handleSelect = (key: string) => {
    onMenuSelect?.(key);
    navigate(key);
  };

  return (
    <div className={cn('z-11 flex-1 overflow-x-hidden overflow-y-auto bg-white', className)}>
      <SidebarMenu>
        {menuItems.map((item) => {
          if (isSubmenuItem(item)) {
            const isActive = selectedSubmenuKeys.includes(item.key);

            return (
              <SidebarMenuItem key={item.key}>
                <DropdownMenu>
                  <DropdownMenuTrigger
                    render={
                      <SidebarMenuButton
                        isActive={isActive}
                        className="data-popup-open:bg-gray-200 data-popup-open:text-gray-900 data-open:bg-gray-200 data-open:text-gray-900"
                      >
                        {item.icon}
                        <span>{item.label}</span>
                        <ChevronRightIcon
                          data-slot="sidebar-expand-icon"
                          className="ml-auto size-3 shrink-0 text-gray-600"
                        />
                      </SidebarMenuButton>
                    }
                  />
                  <DropdownMenuContent
                    side="right"
                    align="start"
                    sideOffset={0}
                    className="w-25 bg-gray-100 p-0"
                  >
                    {item.children.map((child) => (
                      <DropdownMenuItem
                        key={child.key}
                        className={cn(
                          'rounded-none bg-gray-100 py-2.5 focus:bg-gray-200',
                          selectedKeys.includes(child.key) &&
                            'bg-green-400/30 text-gray-900 focus:bg-green-400/30',
                        )}
                        onClick={() => handleSelect(child.key)}
                      >
                        {child.label}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              </SidebarMenuItem>
            );
          }

          return (
            <SidebarMenuItem key={item.key}>
              <SidebarMenuButton
                isActive={selectedKeys.includes(item.key)}
                onClick={() => handleSelect(item.key)}
              >
                {item.icon}
                <span>{item.label}</span>
              </SidebarMenuButton>
            </SidebarMenuItem>
          );
        })}
      </SidebarMenu>
    </div>
  );
};

export default AppMenu;
